/** Listeners told when the subscription socket comes back after a drop; events in between are lost. */
const listeners = new Set<() => void>();

/** Calls `listener` after every reconnect of the subscription socket; returns the unsubscribe. */
export function onSubscriptionsReconnected(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Called by the subscription client once a retried connection is acknowledged. */
export function notifySubscriptionsReconnected(): void {
  listeners.forEach(listener => listener());
}

/** The longest the subscription socket waits before its next reconnect attempt. */
export const SUBSCRIPTION_RETRY_MAX_WAIT_MS = 30_000;
const SUBSCRIPTION_RETRY_BASE_MS = 1_000;
const SUBSCRIPTION_RETRY_JITTER_MS = 3_000;

/** Exponential backoff from one second with jitter, capped at `SUBSCRIPTION_RETRY_MAX_WAIT_MS`. */
export function subscriptionRetryDelayMs(retries: number, random: () => number = Math.random) {
  const backoff = SUBSCRIPTION_RETRY_BASE_MS * 2 ** retries;
  return Math.min(
    backoff + Math.floor(random() * SUBSCRIPTION_RETRY_JITTER_MS),
    SUBSCRIPTION_RETRY_MAX_WAIT_MS
  );
}

/** graphql-ws retry options: reconnect forever, so live updates resume after any outage. */
export const subscriptionRetry = {
  retryAttempts: Infinity,
  shouldRetry: () => true,
  retryWait: (retries: number) =>
    new Promise<void>(resolve => setTimeout(resolve, subscriptionRetryDelayMs(retries)))
};
